import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, H5, LocaleContext, ModalWindow, Spinner, useLocalizeMessage } from 'libs.nucleus';
import { EM_DASH, ListInformation } from 'libs.react';
import { AuthContext } from 'libs.react_internal';
import { LogEvents } from '../../constants/log_events.constants';
import { StudyTrainingContext } from '../../contexts/study_training/study_training.context';
import { generatePDF } from '../../helpers/generate_pdf.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useLumosEventLogger } from '../../hooks/use_lumos_event_logger';
import { useLumosTrainingProgress } from '../../hooks/use_lumos_training';
import { CertificateOfCompletion } from '../lumos_training/components/certificate_of_completion.component';
var CERTIFICATE_TYPE;
(function (CERTIFICATE_TYPE) {
    CERTIFICATE_TYPE["MEDABLE_TRAINING"] = "medable_training";
    CERTIFICATE_TYPE["STUDY_TRAINING"] = "study_training";
})(CERTIFICATE_TYPE || (CERTIFICATE_TYPE = {}));
export const TrainingList = () => {
    const translate = useLocalizeMessage();
    const { environment } = useCurrentEnvironment();
    const { logLumosEvent } = useLumosEventLogger();
    const { siteUserModuleProgress, trainingVersion } = useLumosTrainingProgress();
    const currentLocale = useContext(LocaleContext);
    const { user, entity } = useContext(AuthContext);
    const { hasPassedTrainingCheck, hasStudyTraining, studyTrainingInfo } = useContext(StudyTrainingContext);
    const certificateRef = useRef(null);
    const [visibleCompletionCertificateModalType, setVisibleCompletionCertificateModalType] = useState(null);
    const handleDownloadCertificateBtnClick = (val) => {
        // Safety check: prevent opening study training modal if there's no study training
        if (val === CERTIFICATE_TYPE.STUDY_TRAINING && !hasStudyTraining) {
            return;
        }
        setVisibleCompletionCertificateModalType(val);
    };
    const handleCloseCompletionCertificateModal = () => {
        setVisibleCompletionCertificateModalType(null);
    };
    useEffect(() => {
        // Log event if the training certificate is being generated with a null completedOn
        if (siteUserModuleProgress && !siteUserModuleProgress.completedOn) {
            logLumosEvent(LogEvents.TRAINING_CERTIFICATE_WITH_NULL_COMPLETED_ON, {
                identityId: siteUserModuleProgress?.identityId,
                trainingType: CERTIFICATE_TYPE.MEDABLE_TRAINING,
            });
        }
        if (hasStudyTraining && studyTrainingInfo && !studyTrainingInfo.completedOn) {
            logLumosEvent(LogEvents.TRAINING_CERTIFICATE_WITH_NULL_COMPLETED_ON, {
                identityId: studyTrainingInfo?.identityId,
                trainingType: CERTIFICATE_TYPE.STUDY_TRAINING,
            });
        }
    }, [siteUserModuleProgress?.completedOn, studyTrainingInfo?.completedOn, hasStudyTraining]);
    const listItems = useMemo(() => {
        if (!user || !entity) {
            return [];
        }
        let studyTrainingStatus = EM_DASH;
        let studyTrainingActionContent = _jsx(_Fragment, {});
        if (hasStudyTraining && hasPassedTrainingCheck) {
            studyTrainingStatus = translate('generic.completed');
            studyTrainingActionContent = (_jsx(Button, { onClick: () => handleDownloadCertificateBtnClick(CERTIFICATE_TYPE.STUDY_TRAINING), variant: 'transparent', size: 'sm', label: translate('action.download_certificate') }));
        }
        return [
            {
                title: translate('label.medable_training'),
                dataTestId: 'medable-training',
                description: translate('generic.completed'),
                actionContent: (_jsx(Button, { onClick: () => handleDownloadCertificateBtnClick(CERTIFICATE_TYPE.MEDABLE_TRAINING), variant: 'transparent', size: 'sm', label: translate('action.download_certificate') })),
            },
            ...(hasStudyTraining
                ? [
                    {
                        title: translate('info.lumos_training.certificate.study_training', {
                            studyName: environment?.study?.name || '',
                        }),
                        dataTestId: 'study-training',
                        description: studyTrainingStatus,
                        actionContent: studyTrainingActionContent,
                    },
                ]
                : []),
        ];
    }, [entity, user, hasPassedTrainingCheck, hasStudyTraining, environment]);
    const medableTrainingCertificate = useMemo(() => {
        const { identityId, entityId: trainingEntityId, id: trainingId, completedOn: trainingCompletionDateISO, } = siteUserModuleProgress || {};
        const trainingCompletionDate = trainingCompletionDateISO
            ? new Date(trainingCompletionDateISO).toLocaleDateString(currentLocale, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            })
            : EM_DASH;
        // Confirmation number is a combination of identityId, entityId and trainingId
        let certificateConfirmationNumber = EM_DASH;
        if (identityId && trainingEntityId && trainingId) {
            certificateConfirmationNumber = `${identityId}-${trainingEntityId}-${trainingId}`;
        }
        return {
            completionDate: trainingCompletionDate,
            confirmationNumber: certificateConfirmationNumber,
        };
    }, [currentLocale, siteUserModuleProgress]);
    const studyTrainingCertificate = useMemo(() => {
        if (!hasStudyTraining) {
            return {
                completionDate: EM_DASH,
                confirmationNumber: EM_DASH,
            };
        }
        const { identityId, studyId, completedOn: trainingCompletionDateISO } = studyTrainingInfo || {};
        const trainingCompletionDate = trainingCompletionDateISO
            ? new Date(trainingCompletionDateISO).toLocaleDateString(currentLocale, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            })
            : EM_DASH;
        // Confirmation number is a combination of identityId and studyId
        let certificateConfirmationNumber = EM_DASH;
        if (identityId && studyId) {
            certificateConfirmationNumber = `${identityId}-${studyId}`;
        }
        return {
            completionDate: trainingCompletionDate,
            confirmationNumber: certificateConfirmationNumber,
        };
    }, [currentLocale, studyTrainingInfo, hasStudyTraining]);
    const renderCertificateNode = ({ certificateType = visibleCompletionCertificateModalType, displayMode = 'web', }) => {
        let nodeToRender;
        if (certificateType === CERTIFICATE_TYPE.STUDY_TRAINING) {
            // Safety check: only render study training certificate if study training is available
            if (!hasStudyTraining || !studyTrainingInfo) {
                return null;
            }
            nodeToRender = (_jsx(CertificateOfCompletion, { displayMode: displayMode, user: user, studyName: environment?.study?.name || '', completionDate: studyTrainingCertificate.completionDate, confirmationNumber: studyTrainingCertificate.confirmationNumber }));
        }
        else if (certificateType === CERTIFICATE_TYPE.MEDABLE_TRAINING) {
            nodeToRender = (_jsx(CertificateOfCompletion, { displayMode: displayMode, isStudySpecificTraining: false, user: user, version: trainingVersion, completionDate: medableTrainingCertificate.completionDate, confirmationNumber: medableTrainingCertificate.confirmationNumber, workspaceName: entity?.name }));
        }
        return nodeToRender;
    };
    return (_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsx(H5, { children: translate('label.training') }), _jsxs("div", { className: 'flex-1 overflow-auto relative', children: [!user && _jsx(Spinner, { wrapper: 'full' }), _jsx(ListInformation, { items: listItems })] }), _jsx(ModalWindow, { title: '', isOpen: visibleCompletionCertificateModalType === CERTIFICATE_TYPE.MEDABLE_TRAINING, hideCloseIcon: true, closeWindow: () => { }, footerPrimaryActionButton: {
                    label: translate('generic.done'),
                    onClick: handleCloseCompletionCertificateModal,
                }, footerSecondaryActionButtons: [
                    {
                        label: translate('generic.download'),
                        onClick: () => generatePDF(certificateRef, 'medable_site_training_certificate_of_completion'),
                    },
                ], width: 'lg', removeBodyPadding: true, children: _jsx("div", { "data-testid": 'certificate-of-completion-modal-content', children: renderCertificateNode({
                        certificateType: CERTIFICATE_TYPE.MEDABLE_TRAINING,
                    }) }) }), _jsx(ModalWindow, { title: '', isOpen: visibleCompletionCertificateModalType === CERTIFICATE_TYPE.STUDY_TRAINING, hideCloseIcon: true, closeWindow: () => { }, footerPrimaryActionButton: {
                    label: translate('generic.done'),
                    onClick: handleCloseCompletionCertificateModal,
                }, footerSecondaryActionButtons: [
                    {
                        label: translate('generic.download'),
                        onClick: () => generatePDF(certificateRef, 'study_training_certificate_of_completion'),
                    },
                ], width: 'lg', removeBodyPadding: true, children: _jsx("div", { "data-testid": 'certificate-of-completion-modal-content', children: renderCertificateNode({
                        certificateType: CERTIFICATE_TYPE.STUDY_TRAINING,
                    }) }) }), _jsx("div", { ref: certificateRef, className: 'w-full bg-white my-16 p-8 border border-gray-300 rounded-lg shadow-md', style: {
                    height: 'auto',
                    width: '950px',
                    display: visibleCompletionCertificateModalType ? 'block' : 'none',
                    position: 'absolute',
                    left: '-9999px',
                    top: '-9999px',
                }, children: renderCertificateNode({
                    displayMode: 'pdf',
                }) })] }));
};
